import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Button, FooterActionBar, Typography } from '@ghq-abi/design-system-v2';
import { useTolgee, useTranslate } from '@tolgee/react';

import { StyledPageContent } from '~/core/templates/App/styles';
import { Skeletons } from '~/shared/components';
import { Card } from '~/shared/components/Card';
import { Collapsible } from '~/shared/components/Collapsible';
import { WelcomeBanner } from '~/shared/components/WelcomeBanner';
import { ETolgeeNamespaces } from '~/shared/enums/ETolgeeNamespaces';
import { sanitizeHtml } from '~/shared/utils/sanitizeHtml';

import { useGetHomeContent } from '../api/useGetHomeContent';
import { useGetHomeFaq } from '../api/useGetHomeFaq';
import type { HomeContentItem, HomeFAQItem } from '../types';

export function Page() {
  const { t } = useTranslate(ETolgeeNamespaces.HOME);
  const { push } = useRouter();
  const { getLanguage } = useTolgee();

  const locale = getLanguage() || 'pt-BR';

  const {
    data: homeContent,
    isLoading: isLoadingHomeContent,
    isError: isErrorHomeContent
  } = useGetHomeContent();

  const {
    data: faqData,
    isLoading: isLoadingFaq,
    isError: isErrorFaq
  } = useGetHomeFaq();

  const [sanitizedHomeContent, setSanitizedHomeContent] = useState('');
  const [sanitizedFaqList, setSanitizedFaqList] = useState<HomeFAQItem[]>([]);

  const homeContentHTML = homeContent
    ? homeContent.find(
        (content: HomeContentItem) =>
          content.locale === locale && content.slug === 'iapp-home'
      )?.contentHtml || ''
    : '';

  const faqList = faqData ?? [];
  const lastFaqIndex = faqList.length - 1;

  useEffect(() => {
    const list = faqData ?? [];
    let cancelled = false;

    async function runSanitization() {
      const [homeSanitized, ...faqSanitized] = await Promise.all([
        sanitizeHtml(homeContentHTML),
        ...list.map(faq => sanitizeHtml(faq.content))
      ]);

      if (cancelled) {
        return;
      }

      const replaceLink = '<a target="_blank" rel="noopener noreferrer"';

      setSanitizedHomeContent(homeSanitized.replaceAll('<a', replaceLink));
      setSanitizedFaqList(
        list.map((faq, index) => ({
          ...faq,
          content: (faqSanitized[index] ?? '').replaceAll('<a', replaceLink)
        })) as HomeFAQItem[]
      );
    }

    void runSanitization();
    return () => {
      cancelled = true;
    };
  }, [homeContentHTML, faqData]);

  const handlePushToPensionDetails = async () => {
    await push('/my-plan');
  };

  if (isLoadingHomeContent || isLoadingFaq) {
    return <Skeletons quantity={5} height={240} width="100%" />;
  }

  return (
    <>
      <Head>
        <title>{t('home.title')}</title>
      </Head>

      <StyledPageContent as="div">
        <div className="pb-16">
          <WelcomeBanner />

          <div className="flex flex-col gap-6 pt-6">
            <Card.Root data-testid="card-root">
              <Card.Header>{t('home.howItWorks.title')}</Card.Header>
              <Card.Content id="home-content">
                <div
                  className="leading-[1.6]"
                  dangerouslySetInnerHTML={{
                    __html: sanitizedHomeContent
                  }}
                />
              </Card.Content>
            </Card.Root>

            <div className="flex flex-col">
              <div className="pb-4 pt-2">
                <Typography
                  className="text-2xl text-text-dark"
                  variant="display-sm-bold"
                  as="h2">
                  {t('home.faq.title')}
                </Typography>
                <Typography variant="body-md-regular" className="mt-2">
                  {t('home.faq.subtitle')}
                </Typography>
              </div>

              {(isErrorHomeContent || isErrorFaq) && !homeContent && (
                <Typography variant="body-md-regular" color="light">
                  {t('loadingErrorMessage')}
                </Typography>
              )}

              <div className="overflow-hidden rounded-[8px]">
                <Collapsible.Root>
                  {sanitizedFaqList.map((faq, index) => (
                    <Collapsible.Item
                      key={faq.id}
                      label={faq.title}
                      isFirst={index === 0}
                      isLast={index === lastFaqIndex}>
                      <div
                        className="faq-content [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_p]:mb-2"
                        dangerouslySetInnerHTML={{
                          __html: faq.content
                        }}
                      />
                    </Collapsible.Item>
                  ))}
                </Collapsible.Root>
              </div>
            </div>
          </div>
        </div>

        <FooterActionBar className="z-49">
          <Button
            variant="primary"
            onClick={handlePushToPensionDetails}
            data-testid="push-button">
            {t('accessMyPlan')}
          </Button>
        </FooterActionBar>
      </StyledPageContent>
    </>
  );
}
